/**
 * Document Review Status Configuration
 *
 * Centralized configuration for document review statuses with proper color mappings.
 * This file provides consistent status definitions across the application.
 *
 * EDS StatusLabel Type Mapping:
 * - pending (yellow/amber): notReceived, clarificationNeeded
 * - draft (gray/blue): received, underReview
 * - success (green): approved
 * - error (red): rejected
 */

/**
 * Document review status configuration map
 * Each status has:
 * - type: EDS StatusLabel type for color display (draft, pending, success, error, default)
 * - labelId: i18n message ID for the status text
 * - defaultText: Fallback text if i18n is not available
 */
export const documentReviewStatusConfig = {
    notReceived: {
        type: 'default',
        labelId: 'documentReviewStatusNotReceived',
        defaultText: 'Not Received'
    },
    received: {
        type: 'success',
        labelId: 'documentReviewStatusReceived',
        defaultText: 'Received'
    },
    underReview: {
        type: 'pending',
        labelId: 'documentReviewStatusUnderReview',
        defaultText: 'Under Review'
    },
    clarificationNeeded: {
        type: 'draft',
        labelId: 'documentReviewStatusClarificationNeeded',
        defaultText: 'Clarification Needed'
    },
    approved: {
        type: 'success',
        labelId: 'documentReviewStatusApproved',
        defaultText: 'Approved'
    },
    rejected: {
        type: 'error',
        labelId: 'documentReviewStatusRejected',
        defaultText: 'Rejected'
    }
};

/**
 * Get the status configuration for a given status value
 * @param {string} status - The status value (e.g., 'notReceived', 'approved')
 * @returns {Object} Status configuration with type, labelId, and defaultText
 */
export const getDocumentReviewStatusConfig = (status) => {
    return documentReviewStatusConfig[status] || {
        type: 'draft',
        labelId: null,
        defaultText: status || 'Unknown'
    };
};

/**
 * Get the EDS StatusLabel type for a given status
 * @param {string} status - The status value
 * @returns {string} EDS StatusLabel type ('success', 'pending', 'error', 'draft', or 'default')
 */
export const getDocumentReviewStatusLabelType = (status) => {
    const config = getDocumentReviewStatusConfig(status);
    return config.type || 'default';
};

/**
 * List of all valid document review statuses
 * Useful for validation and iteration
 */
export const documentReviewStatuses = Object.keys(documentReviewStatusConfig);

/**
 * Default status when no status is set
 */
export const DEFAULT_DOCUMENT_REVIEW_STATUS = 'underReview';

export default documentReviewStatusConfig;
